import Component from 'vue-class-component';
import draggable from 'vuedraggable';
import Moment from 'moment';
import { Inject, Mixins, Prop } from 'vue-property-decorator';
import KEYS from '@/values/keys';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { SECTION_OPTIONS_ROOT_TEMPLATES, SECTION_TYPES } from '@/content/values/sections';
import { SectionsProvider } from '@/content/providers/sections.provider'
import { SECTION_OPTION_MODE } from '@/courses/values/courses.values';
import { SectionMixin } from '@/courses/mixins/section.mixin';
import { Section } from '@/courses/models/section.model';
import { IParamsModalConfirmation } from '@/plugins/ed-modal/interfaces/params-modal-confirmation.interface';
import { CreateObjectsMixin } from '@/courses/mixins/create-objects.mixin';
import { ReorderSectionOptionsParams } from '@/courses/params/reorder-section-options.params';
import { SCREEN_MIRRORING_EVENTS } from '@/values/screen.mirroring.values';
import { EdContextMenuOptionsModel } from '@/plugins/ed-context-menu/ed-context-menu.plugin';
import { COURSE_EVENTS } from '@/content/values/events';
import { SectionProvider } from '@/sections/providers/section.provider';
import { FixedSection, SectionDraggableGroup } from '@/sections/types/sections.types';
import ToolService from '@/providers/tools';
import { ContentType } from '@/content/types/content.types';

@Component({
  name: 'ed-section',
  components: {
    draggable,
    EdFolderItem: () => import(/* EdFolderItem */'@/sections/components/ed-folder-item/ed-folder-item.component.vue'),
    EdDeckItem: () => import(/* EdDeckFolder */'@/sections/components/ed-deck-item/ed-deck-item.component.vue'),
  }
})
export default class EdSectionComponent extends Mixins(CreateObjectsMixin, SectionMixin) {
  @Inject() EduacAppSetLoading;

  @Prop() readonly parentId: number;
  @Prop() readonly level: number;
  @Prop() readonly minDate: string;
  @Prop({ default: 'SUBJECT' }) readonly contentType: ContentType;
  @Prop({ default: false }) readonly hasParentLesson: boolean;
  @Prop({ default: false }) readonly isProject: boolean;
  @Prop({ default: null }) readonly fixedSection: FixedSection;
  @Prop({ default: false}) readonly hideOptions: boolean;

  isOpened = false;
  isLoaded = false;
  
  isSelected = false;
  cacheTimeout = null;
  dropFilesVisible = false;
  isLoading = false;
  showTaggedInfo = true;

  get showTeacherOnly(): boolean {
    return this.fixedSection !== 'ADDITIONAL';
  }

  get isTemplate(): boolean {
    return this.contentType === 'TEMPLATE';
  } 

  get isLesson() {
    return this.section.fixedSection === 'LESSON'
  }

  get hideOpt() {
    return this.hideOptions || this.section.type === SECTION_TYPES.SECTION_LINK.code;
  }

  get isDayContent () {
    return this.contentType === 'DAY_CONTENT';
  }

  get group(): any {
    let name: SectionDraggableGroup = 'FOLDER_BRANCH';

    if (this.isDeck || this.isDeckObject) {
      return {
        name: 'DECK_CARDS_BRANCH',
        put: (to, from) => false
      };
    }

    if (this.isTemplate) {
      name = 'FOLDER_LESSON_BRANCH';

      if (this.hasParentLesson || this.isLesson) {
        return { 
          name: 'FOLDER_OBJECTS_BRANCH',
          put: (to, from) => {
            const section: Section = from.el.__vue__.context.element;

            if (section) {
              const isFromLesson = section.fixedSection === 'LESSON';
              const hasFromLesson = SectionProvider.getHasSectionLessons(section.sections);
              const isFromDeckLibrary = section.type === 'CARD';

              return !hasFromLesson && !isFromLesson &&!isFromDeckLibrary;
            }
          }
        }
      }
    }

    return { name };
  }

  get name() {
    return ToolService.exitSpecialChars(this.section.name);
  }

  get folderSubtitle(): string {
    let lessonLabel = this.$i18n.t('common.lesson').toString();
    if(this.isLesson && this.section.fixedSequence) {
      lessonLabel += ` ${this.section.fixedSequence}`;
      if(lessonLabel !== this.section.name) {
        return lessonLabel;
      }
    }
    return '';
  }

  get folderIcons() {
    return SectionProvider.getFolderIcons(this.section.fixedSection);
  }

  get hasChildParentLesson() {
    return this.hasParentLesson || (this.isLesson && this.isTemplate);
  }

  get canChildCreateLesson(): boolean {
    return !this.isLesson && this.isTemplate;
  }

  get titleId() {
    return `${KEYS.PRE_SECTION_ID}${this.section.sectionId}`;
  }

  get isMovingObject() {
    return this.$store.state.CourseStore.movingObject;
  }

  get sections() {
    return this.course.sections;
  }

  get hasSubSections() {
    return this.section.type == SECTION_TYPES.SECTION.code || this.section.type == SECTION_TYPES.SECTION_LINK.code;
  }

  get isFolder() {
    return this.section.type == SECTION_TYPES.SECTION.code;
  }

  get isDeck() {
    return this.section.type == SECTION_TYPES.SECTION.code && this.section.fixedSection === 'CARDS_DECK';
  }

  get isDeckObject() {
    return this.section.type == SECTION_TYPES.CARD.code;
  }

  get isObject() {
    return this.section.type !== SECTION_TYPES.SECTION.code && this.section.type !== SECTION_TYPES.SECTION_LINK.code;
  }

  get sublevel() {
    return this.level ? (this.level + 1) : 1;
  }

  get paddingLeft() {
    return `edg-pad-left--${(this.sublevel + 1) * 20}`;
  }

  get sectionClasses() {
    return {
      'ed-section--teacher-only': this.section.teacherOnly,
      'ed-section--opened': (this.isOpened && this.isFolder),
      'ed-section--not-opened': (!this.isOpened && this.isFolder)
    }
  }

  get sectionId() {
    return this.section.sectionId;
  }

  get sectionListId() {
    return `LIST${this.section.sectionId}`;
  }

  get minDateValue() {
    let startDay = this.section.submissionDates ? this.section.submissionDates.startSchoolDay : null;
    let startDate = this.getSchoolDayDate(startDay);
    let minDate = this.minDate;
    return startDate ? startDate : minDate;
  }

  get menuEditFolderOptions() {
    return SectionsProvider.getEditSectionMenu(this.$i18n);
  }

  get menuAddFolderOptions() {
    const i18n: any = this.$i18n;
    if(this.isDayContent) {
      return SectionsProvider.getCreateObjectMenuTeacher(
        i18n,
        false,
        false
      );
    }

    const filterMaterials = JSON.parse(JSON.stringify(this.course.materials));
    let canCreateLesson = false;
    if(this.isTemplate) {
      if(!this.hasParentLesson && !this.isLesson) {
        return SectionsProvider.getCreateObjMenu(SECTION_OPTIONS_ROOT_TEMPLATES, i18n);
      }
    } 
    return SectionsProvider.getCreateObjectMenuTeacher(
      i18n,
      SectionsProvider.checkCourseMaterials(filterMaterials),
      canCreateLesson
    );
  }

  get allSections() {
    if (!this.fixedSection) {
      return this.course.sections.filter(s => 
        s.fixedSection !== 'FUN' && s.fixedSection !== 'FILLERS' && s.fixedSection !== 'ADDITIONAL'
      );
    } else {
      return this.course.sections.find(s => 
        s.fixedSection === this.fixedSection  
      )?.sections || [];
    }
  }

  setShowTaggedInfo() {
    this.showTaggedInfo = false;
  }

  selectSection() {
    this.$edEventBus.$emit(COURSES_EVENTS.SECTION_SELECTED, {
      sectionId: this.section.sectionId,
      courseId: this.courseID,
      classId: this.classID
    });
    this.$edEventBus.$emit(COURSES_EVENTS.UNSELECT_PROJECTS);
  }

  setSelected({ sectionId, courseId = -1, classId }: any) {
    if (this.options.selectable) {
      let classIdCondition: boolean = this.options.editable ? true : classId == this.classID
      if (this.courseID == courseId && classIdCondition) {
        this.isSelected = sectionId == this.section.sectionId;
      }
    }
  }

  openSection(emitMirroringEvent: boolean = true) {
    if(!this.isDayContent) this.goToSection();

    this.$edEventBus.$emit(COURSES_EVENTS.SECTION_OPENED, {
      sectionId: this.section.sectionId,
      opened: !this.isOpened,
      courseId: this.courseID,
      classId: this.classID
    });

    if (emitMirroringEvent) {
      var target = this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_SECTION_FOLDER);
      this.$edEventBus.$emit(SCREEN_MIRRORING_EVENTS.CLICK, this, { target: target });
    }
  }

  openDeck(cardId = null, edition = false) {
    this.goToDeck(cardId, edition);

    this.$Courses.$emit(COURSES_EVENTS.SELECT_CARD, this.section);
  }

  editDeck() {
    this.goToEditDeck();
  }

  checkChildren(section, sectionId) {
    if (!section || !section.sectionId)
      return false;
    else if (section.sectionId == sectionId) {
      return true;
    }
    else if (!section.sections) {
      return false;
    }
    var result = false;
    section.sections.forEach(subSection => {
      result = this.checkChildren(subSection, sectionId) || result;
    });
    return result;
  }

  setOpened({ sectionId, courseId = -1, opened, classId }) {
    if (this.$store.state.CourseStore.sectionOpenedMode == SECTION_OPTION_MODE.STRICT && !this.options.editable) {
      if (courseId == this.courseID && classId == this.classID) {
        if (sectionId === this.section.sectionId) {
          this.isOpened = opened;
        } else if (this.checkChildren(SectionsProvider.getSectionByID(this.sections, this.section.sectionId), sectionId)) {
          this.isOpened = true;
        } else {
          this.isOpened = false;
        }
      }
    } else if (sectionId == this.section.sectionId) {
      this.isOpened = opened;
      this.isLoaded = true;
    }
  }

  goToSection() {
    this.selectSection();
    if (this.options.editable) {
      if(this.section.type !== SECTION_TYPES.SECTION_LINK.code && this.section.fixedSection !== SECTION_TYPES.SECTION_LINK.code) {
        const isLesson = this.section.fixedSection === 'LESSON';
        this.goToEditSection(this.minDate, isLesson);
      } else {
        // Go To Blank page when is a LINK Lesson
        let selectInfo = {
          courseId: this.courseID,
          classId: this.classID, 
          componentInfo: { 
            name: null,
            section: null,
            edition: false
          }
        };
        this.$Courses.Store.setPreviewSelected({
          section: null,
          material: null,
          courseId: this.courseID,
          classId: this.classID
        });
        this.goToComponentSelected(selectInfo);
      }
    } else {
      this.setFolderInStorage(this.section.sectionId, this.parentId);
      let data = {
        sectionId: this.section.sectionId,
        courseId: this.courseID,
        classId: this.classID
      };
      if (this.section.type !== SECTION_TYPES.SECTION.code && this.section.type !== SECTION_TYPES.SECTION_LINK.code) {
        this.goToSectionPreview(data);
      } else {
        this.setTeacherPosition({ ...data, isFolder: true });
      }
    }
  }

  async onSectionMoved(e: any) {
    this.$Courses.Store.setMovingObject(false);

    if (this.isDeck || this.isDeckObject) {
      this.$Courses.Store.reorderSectionA({
        section: e.item._underlying_vm_,
        parentId: this.section.sectionId,
        place: e.newIndex == 0 ? -1 : this.section.sections[(e.newIndex - 1)] ? this.section.sections[(e.newIndex - 1)].id : this.section.sections[e.newIndex].id,
        courseId: this.courseID
      });

      this.$Courses.$emit(COURSES_EVENTS.SELECT_CARD, e.item._underlying_vm_);

      return;
    }

    if (this.isTemplate) {
      SectionProvider.confirmSectionMoveInTemplate(
        e.item._underlying_vm_,
        e.oldIndex,
        this.allSections,
        this.section.sectionId
      ).then(async ({ modalUsed }) => {
        if (modalUsed) {
          this.EduacAppSetLoading(true);
        }

        await this.$Courses.Store.reorderSectionA({
          section: e.item._underlying_vm_,
          parentId: this.section.sectionId,
          place: e.newIndex == 0 ? -1 : this.section.sections[(e.newIndex - 1)] ? this.section.sections[(e.newIndex - 1)].id : this.section.sections[e.newIndex].id,
          courseId: this.courseID
        });

        await this.getContentForCourse({
          courseId: this.courseID,
          classId: this.classID
        });
      }).finally(() => {
        this.EduacAppSetLoading(false);
      });
    } else {
      this.$Courses.Store.reorderSectionA({
        section: e.item._underlying_vm_,
        parentId: this.section.sectionId,
        place: e.newIndex == 0 ? -1 : this.section.sections[(e.newIndex - 1)] ? this.section.sections[(e.newIndex - 1)].id : this.section.sections[e.newIndex].id,
        courseId: this.courseID
      });
    }
  }

  onSectionAdded(e) {
    this.$Courses.Store.setMovingObject(false);
    setTimeout(() => {
      if(this.isTemplate) {
        SectionProvider.confirmSectionMoveInTemplate(
          e.item._underlying_vm_,
          e.oldIndex,
          this.allSections,
          this.section.sectionId
        ).then(async ({ modalUsed }) => {
          if (modalUsed) {
            this.EduacAppSetLoading(true);
          }
          await this.sectionAdded(
            e.item._underlying_vm_,
            new ReorderSectionOptionsParams(),
            e.newIndex
          );
          await this.getContentForCourse({
            courseId: this.courseID,
            classId: this.classID
          });
        }).finally(() => {
          this.EduacAppSetLoading(false);
        });
      } else {
        this.onSectionAddedInSubject(
          e.item._underlying_vm_,
          e.newIndex
        );
      }
    }, 100);
  }

  onSectionAddedInSubject(sectionIn: Section, newIndex: number) {
    if (sectionIn.type == SECTION_TYPES.SECTION.code) {
      if (sectionIn.submissionDates && this.section.submissionDates) {
        if (sectionIn.submissionDates.startSchoolDay && this.section.submissionDates.startSchoolDay) {
          const currentStartDate = Moment(this.section.submissionDates.startDate);
          const inStartDate = Moment(sectionIn.submissionDates.startDate);
          if (currentStartDate.isAfter(inStartDate)) {
            this.validateFolderAdded(sectionIn, newIndex);
            return;
          }
        }
      }
    } else {
      let isManualDueDate = sectionIn.submissionDates && !sectionIn.submissionDates.dueDateAuto;
      let hasDueDate = sectionIn.submissionDates && sectionIn.submissionDates.dueDate;
      let folderHasDate = this.section.submissionDates && this.section.submissionDates.startSchoolDay;
      if (folderHasDate && sectionIn.homework && hasDueDate) {
        if (isManualDueDate) {
          this.confirmManualObjectMoved(sectionIn, newIndex);
        } else {
          this.confirmAutoObjectMoved(sectionIn, newIndex);
        }
        return;
      }
    }
    this.sectionAdded(sectionIn, new ReorderSectionOptionsParams(), newIndex);
  }

  confirmManualObjectMoved(sectionIn: Section, newIndex: number) {
    const dueDate = Moment(sectionIn.submissionDates.dueDate).format('MM/DD/YYYY');
    const startDate = Moment(this.section.submissionDates.startDate).format('MM/DD/YYYY');
      this.$EdModal.createConfirmationModal({
        htmlBody: true,
        body: this.$i18n.t('courses.objects.movingObjectDueDate', {
          startDate,
          dueDate
        }),
        actionText: this.$i18n.t('courses.objects.saveAndMove'),
        firstLoadingIsDone: true,
        hasCloseOption: true,
        closeAction: () => {
          this.$edEventBus.$emit(COURSES_EVENTS.REFRESH_WIDGET_COURSE, {
            courseId: this.section.courseId,
            classId: this.section.classId
          });
          this.$EdModal.closeModal();
        },
        actions: [{
          label: this.$i18n.t('courses.objects.changeDueDate'),
          mode: 'outline',
          action: async () => {
            this.sectionAdded(sectionIn, new ReorderSectionOptionsParams(), newIndex);
            this.$EdModal.closeConfirmationModal();
            setTimeout(() => {
              this.$EdModal.createModal(
                SECTION_TYPES[sectionIn.type].modal, {
                parentId: sectionIn.parentId,
                courseId: this.courseID,
                edition: true,
                minDate: this.minDate,
                section: sectionIn
              }
              );
            }, 300);
          }
        }, {
          label: this.$i18n.t('courses.objects.recalculateDueDate'),
          hasLoading: true,
          action: async () => {
            try {
              await this.sectionAdded(
                sectionIn, 
                {
                  recalculateDueDate: true,
                  recalculateLastDate: true
                },
                newIndex
              );
              await this.refreshContent({
                sectionId: sectionIn.sectionId,
                parentId: sectionIn.parentId,
                courseId: sectionIn.courseId,
                classId: sectionIn.classId
              });
              this.$EdModal.closeModal();
            } catch (err) {
              this.$EdModal.closeModal();
            }
          }
        }, {
          label: this.$i18n.t('courses.objects.keepDueDate'),
          action: async () => {
            this.sectionAdded(sectionIn, new ReorderSectionOptionsParams(), newIndex);
            this.$EdModal.closeModal();
          }
        }]
      });
  }

  confirmAutoObjectMoved(sectionIn: Section, newIndex: number) {
    const dueDate = Moment(sectionIn.submissionDates.dueDate).format('MM/DD/YYYY');
    const startDate = Moment(this.section.submissionDates.startDate).format('MM/DD/YYYY');
    this.$EdModal.createConfirmationModal({
      htmlBody: true,
      body: this.$i18n.t('courses.objects.movingObjectAutoDueDate', {
        startDate,
        dueDate
      }),
      firstLoadingIsDone: true,
      actions: [{
        label: this.$i18n.t('common.cancel'),
        mode: 'outline',
        action: async () => {
          this.$edEventBus.$emit(COURSES_EVENTS.REFRESH_WIDGET_COURSE, {
            courseId: this.section.courseId,
            classId: this.section.classId
          });
          this.$EdModal.closeModal();
        }
      }, {
        label: this.$i18n.t('courses.objects.saveAndMove'),
        action: async () => {
          sectionIn.loading = true;
          this.$Courses.Store.updateSection({
            section: sectionIn,
            classId: sectionIn.classId,
            courseId: sectionIn.courseId
          });
          await this.sectionAdded(
            sectionIn, 
            {
              recalculateDueDate: true,
              recalculateLastDate: true
            },
            newIndex
          );
          this.refreshContent({
            sectionId: sectionIn.sectionId,
            parentId: sectionIn.parentId,
            courseId: sectionIn.courseId,
            classId: sectionIn.classId
          });
        }
      }]
    });
  }

  validateFolderAdded(sectionIn: Section, newIndex: number) {
    let params: IParamsModalConfirmation = {
      htmlBody: true,
      body: String(this.$i18n.t('courses.objects.alertStartDate', {
        sourceFolder: `${this.section.name} (${Moment(this.section.submissionDates.startDate).format('MM/DD/YYYY')})`,
        targetFolder: `${sectionIn.name} (${Moment(sectionIn.submissionDates.startDate).format('MM/DD/YYYY')})`,
      })),
      actionText: String(this.$i18n.t('courses.objects.useInheritDate', {
        inheritDate: Moment(this.section.submissionDates.startDate).format('MM/DD/YYYY')
      })),
      secondLoadingIsDone: true,
      firstLoadingIsDone: true,
      action: () => {
        let data: any = { ...sectionIn };
        data.startSchoolDay = this.section.submissionDates.startSchoolDay;
        data.submissionDates = {
          startSchoolDay: this.section.submissionDates.startSchoolDay,
          dueDateAuto: true,
          lastDateAuto: true
        };
        this.$Courses.Store.updateSectionA(data).finally(async() => {
          sectionIn = data;
          this.sectionAdded(sectionIn, new ReorderSectionOptionsParams(), newIndex);
          this.$edEventBus.$emit(COURSES_EVENTS.REFRESH_WIDGET_COURSE, {
            courseId: this.section.courseId,
            classId: this.section.classId
          });
          this.$EdModal.closeModal();
        });
      },
      secondActionText: String(this.$i18n.t('courses.objects.removeDate', {
        dateToRemove: Moment(sectionIn.submissionDates.startDate).format('MM/DD/YYYY')
      })),
      secondAction: (response) => {
        let data: any = { ...sectionIn };
        data.startSchoolDay = '';
        data.autoGeneratedName = false;
        data.submissionDates = {
          startSchoolDay: '',
          dueDateAuto: true,
          lastDateAuto: true
        };
        this.$Courses.Store.updateSectionA(data).finally(() => {
          this.$EdModal.closeModal();
        });
      },
      thirdActionText: String(this.$i18n.t('common.cancel')),
      thirdAction: () => {
        this.$edEventBus.$emit(COURSES_EVENTS.REFRESH_WIDGET_COURSE, {
          courseId: this.section.courseId,
          classId: this.section.classId
        });
      }
    };
    this.$EdModal.createConfirmationModal(params);
  }

  async sectionAdded(section: Section, options = new ReorderSectionOptionsParams(), newIndex: number) {
    let place = 0;

    section.parentId = this.section.sectionId;
    section.teacherOnly = section.teacherOnly || this.section.teacherOnly;

    if (newIndex === 0) {
      place = -1;
    } else if(this.section.sections[newIndex]) {
      if (this.section.sections[newIndex].sectionId === section.id) {
        place = this.section.sections[newIndex - 1].id;
      } else {
        place = this.section.sections[newIndex].sectionId;
      }
    } else {
      place = -1;
    }

    await this.$Courses.Store.reorderSectionA({
      section,
      parentId: this.section.sectionId,
      place,
      courseId: this.courseID,
      options
    });
  }

  getCanDropFile(e) {
    return e.dataTransfer.effectAllowed !== 'move';
  }

  setFolderDragLeave(e) {
    if (this.options.editable && !this.isMovingObject) {
      e.stopPropagation();
      e.preventDefault();
      clearTimeout(this.cacheTimeout);
      this.cacheTimeout = null;
    }
  }

  setFolderDragOver(e) {
    if (this.options.editable && !this.isMovingObject) {
      if (this.getCanDropFile(e)) {
        this.dropFilesVisible = true;
        e.stopPropagation();
        e.preventDefault();
        if (!this.cacheTimeout && !this.isOpened) {
          this.cacheTimeout = setTimeout(() => {
            this.$edEventBus.$emit(COURSES_EVENTS.SECTION_OPENED, {
              sectionId: this.sectionId,
              opened: true,
              courseId: this.courseID,
              classId: this.classID
            });
            this.cacheTimeout = null;
          }, 700);
        }
      } else {
        clearTimeout(this.cacheTimeout);
        this.dropFilesVisible = false;
      }
    }
  }

  setListDragOver(e) {
    if (this.options.editable && !this.isMovingObject) {
      if (this.getCanDropFile(e)) {
        this.dropFilesVisible = true;
        e.stopPropagation();
        e.preventDefault();
        let list = document.getElementById(this.sectionListId);
        list.classList.add('ed-section__list--dragover');
        
        this.$edEventBus.$emit(COURSES_EVENTS.CLOSE_DRAG_ZONE, this.section.sectionId);
      } else {
        this.dropFilesVisible = false;
      }
    }
  }

  setListDragLeave(e) {
    if (this.options.editable && !this.isMovingObject) {
      e.stopPropagation();
      e.preventDefault();
      let list = document.getElementById(this.sectionListId);
      list.classList.remove('ed-section__list--dragover');
    }
  }

  setListDrop(e) {
    if (this.options.editable && !this.isMovingObject) {
      if (this.getCanDropFile(e)) {
        e.stopPropagation();
        e.preventDefault();
        let list = document.getElementById(this.sectionListId);
        list.classList.remove('ed-section__list--dragover');
        let file = e.dataTransfer.files[0];
        if (this.isFileExtensionValid(file.name)) {
          let ext = this.getExtension(file.name);
          let typeObject = (SECTION_TYPES.LESSON_PPT_HTML.extensions.includes(ext.toUpperCase())) 
                              ? SECTION_TYPES.LESSON_PPT_HTML.code : SECTION_TYPES.LESSON_TEMP_FILE.code;
          this.goToCreateObject({
            code: typeObject,
            parentId: this.sectionId,
            contentId: this.courseID,
            classId: this.section.classId,
            minDate: null,
            preload: { file },
            contentType: this.contentType,
          });
        }
        this.dropFilesVisible = false;
      }
    }
  }

  getExtension(filename) {
    let fileArr = filename.split('.');
    return fileArr[fileArr.length - 1] ? fileArr[fileArr.length - 1] : '';
  }

  setWatchDragFiles() {
    if (this.isFolder) {
      let folder = document.getElementById(this.sectionId?.toString());
      folder && folder.addEventListener('dragover', this.setFolderDragOver);
      folder && folder.addEventListener('dragleave', this.setFolderDragLeave);
    }

    const lessonValidation = this.isTemplate ? (this.isLesson || this.hasParentLesson) : true;

    if (this.isFolder && lessonValidation) {
      let list = document.getElementById(this.sectionListId);
      if (list) {
        list.addEventListener('dragover', this.setListDragOver);
        list.addEventListener('dragleave', this.setListDragLeave);
        list.addEventListener('drop', this.setListDrop);
      }
    }
  }

  setFolderOpened(data: any) {
    if (data.sectionSelected) {
      let included = data.listIds.includes(this.sectionId);
      if (!included) this.isOpened = false;
      return;
    }
    this.isOpened = false;
  }

  closeDragZone(sectionId) {
    if (sectionId !== this.section.sectionId) {
      this.dropFilesVisible = false;
    }
  }

  target(screenMirroringEventName: string) {
    return screenMirroringEventName.replace('{sectionId}', this.section.sectionId?.toString());
  }

  async checkChildrenVisibility(section, status) {
    if (section.teacherOnly != status) {
      await this.setTeacherOnlySection(
        this.courseID,
        section.sectionId,
        this.classID
      );
    }
    if (!section.sections) return;
    section.sections.forEach(async (s) => {
      this.checkChildrenVisibility(s, status)
    });
  }

  async toggleVisible() {
    try {
      this.isLoading = true;
      await this.setTeacherOnlySection(
        this.courseID,
        this.section.sectionId,
        this.classID
      );
      if (this.parentId) {
        await this.checkParentTeacherOnly(
          this.courseID,
          this.course.sections,
          this.section,
          this.parentId,
          this.classID
        );
      }
      this.checkChildrenVisibility(this.section, this.section.teacherOnly);
      this.isLoading = false;
    } catch (err) {
      this.isLoading = false;
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('courses.errors.errorSavingObject'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    }
  }

  async selectEditOption(optionSelected: EdContextMenuOptionsModel) {
    if (optionSelected.type) {
      if (optionSelected.type == COURSE_EVENTS.EDIT_SECTION) {
        this.openSection();
      } else {
        this.isLoading = true;
        this.setSelected({ sectionId: this.section.sectionId });
        await this.removeSection({
          sectionId: this.section.sectionId,
          courseId: this.section.courseId,
          classId: this.section.classId
        });
        await this.getContentForCourse({ 
          courseId: this.section.courseId,
          classId: this.section.classId
        });
        this.isLoading = false;
      }
    }
  }

  selectAddOption(optionSelected = new EdContextMenuOptionsModel({})) {
    let modal: any;
    let params: any;
    if(this.isDayContent) return

    switch (optionSelected.code) {

      case SECTION_TYPES.SECTION.code:
        this.selectSection();
        this.goToCreateSection(this.minDateValue);
        this.goToCreateFolderOrLesson(false);
      break;

      case SECTION_TYPES.SECTION_LESSON.code:
        this.selectSection();
        this.goToCreateFolderOrLesson(true);  
      break;

      case SECTION_TYPES.LESSON_IMAGES.code:
      case SECTION_TYPES.LESSON_VIDEO.code:
      case SECTION_TYPES.LESSON_EXTERNAL_LINK.code:
        modal = SECTION_TYPES[optionSelected.code].modal
        params = SectionsProvider.getParamsToSaveSection(
          this.$i18n.t('common.create'),
          this.courseID,
          this.section.sectionId,
          null,
          optionSelected.code,
          this.$i18n.t(`common.${optionSelected.type}`),
          false,
          this.classID
        );

        params.hasTagSelector = this.contentType === 'TEMPLATE';

        this.$EdModal.createModal(modal, params);
        break;

      default:
        this.goToCreateObject({
          code: optionSelected.code,
          parentId: this.section.sectionId,
          contentId: this.courseID,
          classId: this.classID,
          minDate: this.minDateValue,
          contentType: this.contentType,
        });
        break;

    }
  }

  goToCreateFolderOrLesson(isCreatingLesson = false) {
    SectionProvider.openLessonCreator(
      this.section.courseId,
      this.section.classId,
      this.section,
      this.minDateValue,
      isCreatingLesson,
      this.fixedSection
    );
  }

  onGoToEditObject() {
    let optionSelected = this.section.type;
    let params;

    switch(optionSelected) {
      case SECTION_TYPES.LESSON_EXTERNAL_LINK.code:
      case SECTION_TYPES.LESSON_VIDEO.code:
      case SECTION_TYPES.LESSON_IMAGES.code:
        params = SectionsProvider.getParamsToSaveSection(
          this.$i18n.t('common.edit'),
          this.courseID,
          this.parentId,
          this.section.sectionId,
          optionSelected,
          this.section.name,
          true,
          this.classID
        );
       
        params.hasTagSelector = this.contentType === 'TEMPLATE';

        this.$EdModal.createModal(
          SECTION_TYPES[this.section.type].modal,
          params,
        );
      break;
      case SECTION_TYPES.CARD.code:
        this.goToDeck(this.section.id, true);

        setTimeout(() => {
          this.$Courses.$emit(COURSES_EVENTS.OPEN_DECK_EDITOR, this.section);
        }, 100);
      break;
      default:
        this.$EdModal.createModal(
          SECTION_TYPES[optionSelected].modal, {
            parentId: this.parentId,
            courseId: this.courseID,
            edition: true,
            minDate: this.minDate,
            section: this.section,
            contentType: this.contentType,
            classId:  this.classID,
          }
        );
      break;
    }
  }

  async removeDeck() {
    this.$EdModal.createConfirmationModal(
      {
        body: this.$i18n.t('deck.confirmRemoveDeck'),
        action: async () => { this.doRemoveDeck(); }
      }
    );
  }

  async doRemoveDeck() {
    this.isLoading = true;
    await this.$Courses.Store.removeDeckA({
      deckSectionId: this.section.sectionId,
      courseId: this.courseID ,
      classId: this.classID})
    //Refresh all content cause Sub Decks could have been deleted
    await this.getContentForCourse({
      courseId: this.courseID,
      classId: this.classId
    });
    // Redirect to the create deck if the deleted one was opened 
    if(this.section.sectionId === this.course.component?.section?.id) {
      SectionProvider.openDeckCreator(
        this.courseID,
        this.classId,
        this.course.deckLibrary,
        'DECK_LIBRARY'
      );
    }
    this.isLoading = false;
  }

  doAdd() {
    if (this.isDeck) {
      this.goToDeck(null, true);

      setTimeout(() => {
        this.$Courses.$emit(COURSES_EVENTS.OPEN_DECK_EDITOR);
      }, 200);
    }
  }

  created() {
    this.$edEventBus.$on(COURSES_EVENTS.SECTION_OPENED, this.setOpened);
    this.$edEventBus.$on(COURSES_EVENTS.SECTION_SELECTED, this.setSelected);
    this.$edEventBus.$on(COURSES_EVENTS.KEEP_OPEN_FOLDERS, this.setFolderOpened);
    this.$edEventBus.$on(COURSES_EVENTS.CLOSE_DRAG_ZONE, this.closeDragZone);
    this.$edEventBus.$on(this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_SECTION_FOLDER), (args: any) => {
      this.openSection(false);
    });
  }

  mounted() {
    this.setWatchDragFiles();
  }

  beforeDestroy(): void {
    this.$edEventBus.$off(this.target(SCREEN_MIRRORING_EVENTS.ON_OPEN_SECTION_FOLDER));
  }

}
